import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useMembers } from '../hooks/useMembers'
import { useSubscription } from '../hooks/useSubscription'
import { useLinkedHouseholds } from '../features/linkedHouseholds/hooks/useLinkedHouseholds'
import { useSharedMembers } from '../features/linkedHouseholds/hooks/useSharedMembers'
import { Spinner } from '../components/ui/Spinner'
import { cn, getInitials } from '../lib/utils'
import { memberColors } from '../styles/tokens'
import { Portal } from '../components/Portal'

// ─── Icons ────────────────────────────────────────────────────────────────────
const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
)

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)

const CheckCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/>
  </svg>
)

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCode = (uuid) => (uuid ?? '').toUpperCase()

const StatusBadge = ({ status }) => {
  const styles = {
    active:  'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100  text-amber-700',
    revoked: 'bg-rose-100   text-rose-600',
  }
  return (
    <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize', styles[status] ?? styles.revoked)}>
      {status}
    </span>
  )
}

const AVATAR_SIZES = { 7: 'w-7 h-7', 8: 'w-8 h-8', 9: 'w-9 h-9', 10: 'w-10 h-10' }

const Avatar = ({ name, color, size = 8 }) => (
  <div
    className={cn(AVATAR_SIZES[size] ?? 'w-8 h-8', 'rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0')}
    style={{ backgroundColor: memberColors[color ?? 'violet'] }}
  >
    {getInitials(name ?? '')}
  </div>
)

// ─── Countdown ────────────────────────────────────────────────────────────────
const Countdown = ({ expiresAt }) => {
  const [remaining, setRemaining] = useState('')
  useEffect(() => {
    const tick = () => {
      const diff = new Date(expiresAt) - Date.now()
      if (diff <= 0) { setRemaining('Expired'); return }
      const h = Math.floor(diff / 3_600_000)
      const m = Math.floor((diff % 3_600_000) / 60_000)
      const s = Math.floor((diff % 60_000) / 1000)
      setRemaining(`${h}h ${m}m ${s}s`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [expiresAt])
  return <span>{remaining}</span>
}

// ─── Step 1 modal: generate code ──────────────────────────────────────────────
const GenerateCodeModal = ({ onClose, onSuccess }) => {
  const { generateCode } = useLinkedHouseholds()
  const [step,      setStep]      = useState('explain') // explain | code
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')
  const [codeData,  setCodeData]  = useState(null)
  const [copied,    setCopied]    = useState(false)

  const generate = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await generateCode()
      setCodeData(data)
      setStep('code')
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const copy = async () => {
    await navigator.clipboard.writeText(codeData.link_code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-[env(safe-area-inset-bottom,0px)] sm:pb-4">
      <div className="w-full max-w-md bg-surface rounded-2xl overflow-hidden shadow-xl max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="font-bold text-text-primary">Link a Household</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {step === 'explain' && (
          <div className="px-5 py-5 space-y-4 overflow-y-auto flex-1">
            <div className="flex items-start gap-3 p-4 bg-primary-light rounded-xl">
              <span className="text-primary mt-0.5"><LinkIcon /></span>
              <p className="text-sm text-text-secondary leading-relaxed">
                Linking allows another household to use your premium plan and share selected calendars and children&apos;s profiles with your home.
              </p>
            </div>

            <div className="space-y-2 text-sm text-text-secondary">
              <p className="font-semibold text-text-primary text-xs uppercase tracking-wide">What will be shared (your choice):</p>
              {['Selected calendars (read-only for them)', 'Selected children\'s profiles for event tagging'].map((i) => (
                <div key={i} className="flex items-center gap-2 text-emerald-600">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                  <span className="text-text-secondary">{i}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 text-sm text-text-secondary">
              <p className="font-semibold text-text-primary text-xs uppercase tracking-wide">Always kept separate:</p>
              {['Tasks', 'Shopping lists', 'Budget & bills', 'Meals & pantry', 'Vault', 'Notes & rewards'].map((i) => (
                <div key={i} className="flex items-center gap-2 text-text-muted">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  {i}
                </div>
              ))}
            </div>

            {error && <p className="text-sm text-rose-600">{error}</p>}

            <button
              onClick={generate}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white text-sm font-semibold py-3 rounded-xl hover:bg-primary/90 disabled:opacity-60"
            >
              {loading ? <Spinner size="sm" /> : <><LinkIcon />&nbsp;Generate link code</>}
            </button>
          </div>
        )}

        {step === 'code' && codeData && (
          <div className="px-5 py-5 space-y-5 overflow-y-auto flex-1">
            <p className="text-sm text-text-secondary text-center">
              Share this code with the other household&apos;s admin. It expires in:
            </p>

            <div className="text-center">
              <div className="text-xs font-medium text-text-muted mb-2">Expires in</div>
              <div className="text-lg font-bold text-primary tabular-nums">
                <Countdown expiresAt={codeData.expires_at} />
              </div>
            </div>

            <div className="bg-background rounded-xl p-4 flex items-center gap-3">
              <code className="flex-1 text-sm font-mono text-text-primary tracking-wider break-all">
                {formatCode(codeData.link_code)}
              </code>
              <button
                onClick={copy}
                className={cn(
                  'flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors flex-shrink-0',
                  copied ? 'bg-emerald-100 text-emerald-700' : 'bg-surface border border-border text-text-secondary hover:text-primary hover:border-primary'
                )}
              >
                {copied ? <><CheckCircleIcon />&nbsp;Copied</> : <><CopyIcon />&nbsp;Copy</>}
              </button>
            </div>

            <p className="text-xs text-text-muted text-center">
              The other household&apos;s admin goes to Settings → Household → &quot;Enter Link Code&quot; and pastes this code.
            </p>

            <button onClick={() => { onSuccess(); onClose() }}
              className="w-full bg-primary text-white text-sm font-semibold py-3 rounded-xl hover:bg-primary/90">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  )
}

// ─── Step 2 modal: accept code ────────────────────────────────────────────────
const AcceptCodeModal = ({ onClose, onSuccess }) => {
  const { acceptCode } = useLinkedHouseholds()
  const [code,      setCode]      = useState('')
  const [step,      setStep]      = useState('input') // input | confirm
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')
  const [result,    setResult]    = useState(null)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const validate = async () => {
    const trimmed = code.trim()
    if (!trimmed) return
    setLoading(true)
    setError('')
    try {
      const data = await acceptCode(trimmed)
      setResult(data)
      setStep('confirm')
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-[env(safe-area-inset-bottom,0px)] sm:pb-4">
      <div className="w-full max-w-md bg-surface rounded-2xl overflow-hidden shadow-xl max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="font-bold text-text-primary">Enter Link Code</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {step === 'input' && (
          <div className="px-5 py-5 space-y-4 overflow-y-auto flex-1">
            <p className="text-sm text-text-secondary">
              Paste the link code shared by the other household&apos;s admin.
            </p>
            <textarea
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Paste code here…"
              rows={3}
              className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm font-mono text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button
              onClick={validate}
              disabled={loading || !code.trim()}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white text-sm font-semibold py-3 rounded-xl hover:bg-primary/90 disabled:opacity-60"
            >
              {loading ? <Spinner size="sm" /> : 'Connect'}
            </button>
          </div>
        )}

        {step === 'confirm' && result && (
          <div className="px-5 py-5 space-y-5 overflow-y-auto flex-1">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                <CheckCircleIcon />
              </div>
              <p className="font-semibold text-text-primary">Households linked!</p>
              <p className="text-sm text-text-secondary">
                <strong>{result.primary_household_name}</strong> and <strong>{result.linked_household_name}</strong> are now connected.
                Premium features are shared, and you can configure shared calendars and children from the Household Settings.
              </p>
            </div>

            <div className="bg-background rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">What&apos;s shared now:</p>
              {['Premium plan benefits', 'Calendars (you choose which)', 'Children\'s profiles (you choose which)'].map((i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-text-secondary">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="emerald" strokeWidth="2.5" className="text-emerald-500"><path d="M20 6L9 17l-5-5"/></svg>
                  {i}
                </div>
              ))}
            </div>

            <button onClick={() => { onSuccess(); onClose() }}
              className="w-full bg-primary text-white text-sm font-semibold py-3 rounded-xl hover:bg-primary/90">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  )
}

// ─── Shared Children section ──────────────────────────────────────────────────
const SharedChildrenSection = ({ activeLink }) => {
  const { data: members = [] }        = useMembers()
  const { isSharedOut, toggleShare, isLoading } = useSharedMembers()

  const linkedHouseholdId = activeLink.isPrimary
    ? activeLink.linked_household_id
    : activeLink.primary_household_id

  const children = members.filter((m) => m.role === 'limited')

  if (children.length === 0) return (
    <p className="text-sm text-text-muted px-4 py-3">
      No limited members to share. Invite a child or change a member&apos;s role to Limited to see them here.
    </p>
  )

  return (
    <div className="space-y-1">
      {children.map((m) => {
        const name    = m.profiles?.display_name ?? 'Unknown'
        const shared  = isSharedOut(m.user_id)
        return (
          <div key={m.user_id} className="flex items-center justify-between px-4 py-3 bg-surface rounded-xl border border-border">
            <div className="flex items-center gap-3">
              <Avatar name={name} color={m.profiles?.member_color} />
              <div>
                <p className="text-sm font-medium text-text-primary">{name}</p>
                <p className="text-xs text-text-muted">Can be tagged in {activeLink.otherHousehold?.name ?? 'linked household'}&apos;s events</p>
              </div>
            </div>
            <button
              disabled={isLoading}
              onClick={() => toggleShare(m.user_id, linkedHouseholdId)}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors flex-shrink-0',
                shared ? 'bg-primary' : 'bg-border',
              )}
            >
              <span className={cn(
                'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all',
                shared ? 'left-5.5' : 'left-0.5',
              )} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ─── Unlink confirm modal ─────────────────────────────────────────────────────
const UnlinkModal = ({ link, onClose, onUnlinked }) => {
  const { revokeLink } = useLinkedHouseholds()
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  const confirm = async () => {
    setLoading(true)
    setError('')
    try {
      await revokeLink(link.id)
      onUnlinked()
      onClose()
    } catch (e) {
      setError(e.message)
      setLoading(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-[env(safe-area-inset-bottom,0px)] sm:pb-4">
      <div className="w-full max-w-md bg-surface rounded-2xl overflow-hidden shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold text-text-primary">Unlink households?</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-text-secondary leading-relaxed">
            This will remove shared calendars and children from <strong>{link.otherHousehold?.name ?? 'the linked household'}</strong> and they will lose premium access if they have no other paid subscription.
          </p>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 border border-border text-text-secondary text-sm font-semibold py-2.5 rounded-xl hover:bg-background">
              Cancel
            </button>
            <button onClick={confirm} disabled={loading}
              className="flex-1 bg-rose-500 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-rose-600 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <Spinner size="sm" /> : 'Unlink'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  )
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, subtitle, children }) => (
  <div className="mb-8">
    <div className="mb-3">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">{title}</h2>
      {subtitle && <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
)

// ─── Main page ────────────────────────────────────────────────────────────────
export const HouseholdSettingsPage = () => {
  const { household, isAdmin } = useAuth()
  const { data: sub }          = useSubscription()
  const {
    data: links = [],
    isLoading,
    activeLink,
    pendingLink,
  } = useLinkedHouseholds()

  const [modal, setModal] = useState(null) // null | 'generate' | 'accept' | 'unlink'
  const [unlinkTarget, setUnlinkTarget] = useState(null)

  const isPaid = sub?.plan === 'paid' && ['active', 'trialing', 'lifetime'].includes(sub.status ?? '')
    || sub?.is_comped === true

  const canGenerate = isAdmin && isPaid && !activeLink

  const openUnlink = (link) => {
    setUnlinkTarget(link)
    setModal('unlink')
  }

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8 text-center text-sm text-text-muted">
        Only household admins can manage linked households.
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto py-6 px-4">
      <div className="flex items-center gap-2 mb-6">
        <Link to="/settings" className="text-text-muted hover:text-text-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </Link>
        <h1 className="text-xl font-bold text-text-primary">Household Linking</h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          {/* ── Active / pending link ────────────────────────────────────── */}
          {(activeLink || pendingLink) && (
            <Section title="Linked Household">
              {[activeLink, pendingLink].filter(Boolean).map((link) => (
                <div key={link.id} className="bg-surface border border-border rounded-2xl p-4 mb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-light rounded-xl flex items-center justify-center text-primary">
                        <LinkIcon />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-text-primary">
                            {link.otherHousehold?.name ?? (link.status === 'pending' ? 'Awaiting acceptance' : 'Linked household')}
                          </p>
                          <StatusBadge status={link.status} />
                        </div>
                        {link.status === 'active' && !link.isPrimary && (
                          <p className="text-xs text-text-muted mt-0.5">Premium provided by this household</p>
                        )}
                        {link.status === 'pending' && link.isPrimary && (
                          <p className="text-xs text-text-muted mt-0.5">
                            Expires in <Countdown expiresAt={link.expires_at} />
                          </p>
                        )}
                      </div>
                    </div>
                    {(link.status === 'active' || link.isPrimary) && (
                      <button
                        onClick={() => openUnlink(link)}
                        className="text-xs text-rose-500 hover:text-rose-600 font-medium flex-shrink-0 mt-1"
                      >
                        Unlink
                      </button>
                    )}
                  </div>

                  {/* Show pending code if primary + still pending */}
                  {link.status === 'pending' && link.isPrimary && link.link_code && (
                    <div className="mt-4 bg-background rounded-xl p-3 flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono text-text-primary tracking-wide truncate">
                        {formatCode(link.link_code)}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(link.link_code)}
                        className="flex items-center gap-1 text-xs text-text-muted hover:text-primary flex-shrink-0"
                      >
                        <CopyIcon />&nbsp;Copy
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* ── Linked Households actions ─────────────────────────────────── */}
          <Section
            title="Link a Household"
            subtitle={!isPaid ? 'Requires an active paid subscription' : undefined}
          >
            <div className="space-y-2">
              {canGenerate && !pendingLink && (
                <button
                  onClick={() => setModal('generate')}
                  className="w-full flex items-center gap-3 bg-surface border border-border rounded-xl px-4 py-3.5 text-left hover:border-primary/40 transition-colors"
                >
                  <div className="w-9 h-9 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                    <LinkIcon />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Generate link code</p>
                    <p className="text-xs text-text-muted">Share a code with the other household&apos;s admin</p>
                  </div>
                </button>
              )}

              {!activeLink && isAdmin && (
                <button
                  onClick={() => setModal('accept')}
                  className="w-full flex items-center gap-3 bg-surface border border-border rounded-xl px-4 py-3.5 text-left hover:border-primary/40 transition-colors"
                >
                  <div className="w-9 h-9 bg-background rounded-lg flex items-center justify-center text-text-muted flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Enter link code</p>
                    <p className="text-xs text-text-muted">Accept a link invitation from another household</p>
                  </div>
                </button>
              )}

              {!isPaid && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  Upgrade to a paid plan to generate link codes and share your premium with another household.{' '}
                  <Link to="/settings/billing" className="underline font-medium">View plans</Link>
                </div>
              )}
            </div>
          </Section>

          {/* ── Shared Children ──────────────────────────────────────────── */}
          {activeLink && activeLink.isPrimary && (
            <Section
              title="Shared Children"
              subtitle={`Toggle which children can be tagged on events in ${activeLink.otherHousehold?.name ?? 'the linked household'}`}
            >
              <SharedChildrenSection activeLink={activeLink} />
            </Section>
          )}

          {/* ── How it works ─────────────────────────────────────────────── */}
          {!activeLink && !pendingLink && (
            <Section title="How it works">
              <div className="space-y-3">
                {[
                  { step: '1', text: 'The paid household admin generates a link code and shares it.' },
                  { step: '2', text: 'The other household\'s admin enters the code here to connect.' },
                  { step: '3', text: 'Both households share premium, calendars, and children\'s profiles.' },
                ].map(({ step, text }) => (
                  <div key={step} className="flex items-start gap-3 text-sm text-text-secondary">
                    <div className="w-6 h-6 rounded-full bg-primary-light text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {step}
                    </div>
                    {text}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {modal === 'generate' && (
        <GenerateCodeModal onClose={() => setModal(null)} onSuccess={() => setModal(null)} />
      )}
      {modal === 'accept' && (
        <AcceptCodeModal onClose={() => setModal(null)} onSuccess={() => setModal(null)} />
      )}
      {modal === 'unlink' && unlinkTarget && (
        <UnlinkModal
          link={unlinkTarget}
          onClose={() => { setModal(null); setUnlinkTarget(null) }}
          onUnlinked={() => {}}
        />
      )}
    </div>
  )
}
